import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const teamService = {
  // Get all team members for a company
  async getTeamMembers(companyId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.error('Data error:', error?.message);
        return [];
      }

      return toCamelCase(data) || [];
    } catch (error) {
      console.error('Error fetching team members:', error?.message);
      throw error;
    }
  },

  // Get pending invitations for a company
  async getInvitations(companyId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('team_invitations')
        ?.select('*, invited_by:user_profiles!team_invitations_invited_by_fkey(full_name, email)')
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.error('Data error:', error?.message);
        return [];
      }

      return toCamelCase(data) || [];
    } catch (error) {
      console.error('Error fetching invitations:', error?.message);
      throw error;
    }
  },

  // Send team invitation
  async inviteTeamMember(companyId, email, role) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if user already exists in the company
      const { data: existingMember, error: memberCheckError } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('email', email)
        ?.eq('company_id', companyId)
        ?.maybeSingle();

      if (memberCheckError && memberCheckError.code !== 'PGRST116') {
        console.error('Error checking existing member:', memberCheckError);
        throw new Error('Failed to check if user already exists');
      }

      if (existingMember) {
        throw new Error('User is already a member of this team');
      }

      // Check if invitation already exists
      const { data: existingInvitation, error: invitationCheckError } = await supabase
        ?.from('team_invitations')
        ?.select('id, status')
        ?.eq('email', email)
        ?.eq('company_id', companyId)
        ?.maybeSingle();

      if (invitationCheckError && invitationCheckError.code !== 'PGRST116') {
        console.error('Error checking existing invitation:', invitationCheckError);
        throw new Error('Failed to check if invitation already exists');
      }

      if (existingInvitation && existingInvitation?.status === 'pending') {
        throw new Error('An invitation has already been sent to this email');
      }

      console.log('Invoking invite-team-member Edge Function with:', { email, role, companyId });

      // Ensure we have a valid session before calling the Edge Function
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError || !session) {
        throw new Error('You must be signed in to invite team members. Please sign out and sign in again.');
      }

      // Call the invite-team-member Edge Function
      // This function uses Supabase Auth admin API to send invitation emails
      // The user's session token is automatically included by supabase.functions.invoke()
      const { data, error } = await supabase?.functions?.invoke('invite-team-member', {
        body: {
          email: email,
          role: role,
          companyId: companyId
        }
      });

      console.log('Edge Function response:', { data, error });
      console.log('Data type:', typeof data, 'Data content:', JSON.stringify(data));
      console.log('Error type:', typeof error, 'Error content:', error);

      // IMPORTANT: Check data?.error FIRST (when status is non-2xx but response body is JSON)
      // Supabase functions.invoke() may put error responses in data instead of error
      if (data?.error) {
        console.error('Edge Function returned error in data:', data?.error, data?.details);
        // Prioritize details field as it contains the specific error message
        const errorMessage = data?.details || data?.error || 'Failed to send invitation email';
        const err = new Error(errorMessage);
        err.isConfigurationError = data?.configurationRequired || false;
        err.originalError = data?.error;
        err.originalDetails = data?.details;
        err.statusCode = data?.statusCode;
        console.error('Throwing error with message:', errorMessage, 'originalDetails:', data?.details);
        throw err;
      }

      // Then check the error object (for network/connection errors)
      if (error) {
        console.error('Edge Function invocation error:', error);
        console.error('Error name:', error?.name);
        console.error('Error message:', error?.message);
        console.error('Error context:', error?.context);
        
        // Extract detailed error information from Edge Function response
        let errorMessage = 'Failed to send invitation';
        let errorDetails = null;
        let isConfigurationError = false;
        
        // Check if error has context with response details
        if (error?.context) {
          console.error('Error context type:', error.context.constructor?.name);
          console.error('Error context is Response?', error.context instanceof Response);
          
          // The context IS the Response object - read it directly
          if (error.context instanceof Response) {
            try {
              // Clone the response to read it (can only read once)
              const clonedResponse = error.context.clone();
              const responseData = await clonedResponse.json();
              console.error('Read response body from Response object:', responseData);
              errorDetails = responseData;
            } catch (readError) {
              console.error('Could not read response as JSON:', readError);
              // Try as text
              try {
                const clonedResponse = error.context.clone();
                const textBody = await clonedResponse.text();
                console.error('Response body as text:', textBody);
                if (textBody) {
                  errorDetails = JSON.parse(textBody);
                }
              } catch (textError) {
                console.error('Could not parse response as text:', textError);
              }
            }
          } else if (error?.context?.body) {
            // If body is a ReadableStream, we need to read it
            if (error.context.body instanceof ReadableStream) {
              try {
                const reader = error.context.body.getReader();
                const { value, done } = await reader.read();
                if (!done && value) {
                  const decoder = new TextDecoder();
                  const text = decoder.decode(value);
                  errorDetails = JSON.parse(text);
                  console.error('Read response body from ReadableStream:', errorDetails);
                }
                reader.releaseLock();
              } catch (streamError) {
                console.error('Could not read ReadableStream:', streamError);
              }
            } else {
              // Body is already parsed or is a string
              try {
                errorDetails = typeof error?.context?.body === 'string' 
                  ? JSON.parse(error?.context?.body) 
                  : error?.context?.body;
                console.error('Parsed error body:', errorDetails);
              } catch (parseError) {
                console.error('Could not parse error body:', parseError);
                errorDetails = error?.context?.body;
              }
            }
          }
          
          // Check status code
          if (error?.context?.status) {
            console.error('Response status:', error?.context?.status);
          } else if (error?.context instanceof Response) {
            console.error('Response status:', error.context.status);
          }
        }
        
        // Determine the error message to show
        // Prioritize details field as it contains the most specific error message
        if (errorDetails?.details) {
          errorMessage = errorDetails.details;
          // Check if it's a configuration error
          if (errorDetails?.configurationRequired) {
            isConfigurationError = true;
          }
        } else if (errorDetails?.configurationRequired) {
          // This is a configuration error from the Edge Function
          isConfigurationError = true;
          errorMessage = errorDetails?.error || 'Configuration required';
        } else if (errorDetails?.error) {
          // Fallback to error field
          errorMessage = errorDetails?.error;
        } else if (error?.message && error?.message !== 'Edge Function returned a non-2xx status code') {
          errorMessage = error?.message;
        } else {
          // Provide helpful default message
          errorMessage = 'Failed to send invitation. Please try again or contact support if the problem persists.';
        }
        
        // Check for specific error codes
        if (errorDetails?.code === 'INVALID_JWT' || error?.message?.includes('Invalid JWT') || error?.message?.includes('401')) {
          errorMessage = 'Your session has expired. Please sign out and sign in again, then try inviting the team member.';
        }
        
        // Check for service role key configuration errors
        if (errorDetails?.error?.includes('SUPABASE_SERVICE_ROLE_KEY') || errorDetails?.configurationRequired) {
          isConfigurationError = true;
          if (errorDetails?.details) {
            errorMessage = errorDetails?.details;
          } else {
            errorMessage = 'The invitation service needs to be configured. Please set SUPABASE_SERVICE_ROLE_KEY in Edge Function secrets. See EDGE_FUNCTION_SETUP.md for instructions.';
          }
        }
        
        // Create error object with configuration flag and preserve original details
        const err = new Error(errorMessage);
        err.isConfigurationError = isConfigurationError;
        err.originalError = errorDetails?.error;
        err.originalDetails = errorDetails?.details;
        throw err;
      }

      if (!data?.success) {
        console.error('Edge Function did not return success:', data);
        throw new Error(data?.details || data?.message || 'Failed to send invitation email. The Edge Function may not be properly deployed.');
      }

      console.log('Invitation sent successfully:', data);
      return toCamelCase(data?.data);
    } catch (error) {
      console.error('Error inviting team member:', error?.message);
      throw error;
    }
  },

  // Cancel/delete invitation
  async cancelInvitation(invitationId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('team_invitations')?.delete()?.eq('id', invitationId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.error('Data error:', error?.message);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error canceling invitation:', error?.message);
      throw error;
    }
  },

  // Update team member role
  async updateMemberRole(memberId, role) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({ role: role })
        ?.eq('id', memberId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.error('Data error:', error?.message);
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating member role:', error?.message);
      throw error;
    }
  },

  // Remove team member
  async removeMember(memberId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ company_id: null })
        ?.eq('id', memberId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.error('Data error:', error?.message);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error removing member:', error?.message);
      throw error;
    }
  }
};